import React from "react";
import {
  Mail,
  CircleCheck,
  Eye,
  Upload,
  AlarmClock,
  File,
  Clock,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardDescription,
  CardContent,
  CardTitle,
} from "../ui/card";
import { SectionHeader } from "../ui/sectionHeader";
import { DotSeprated } from "../comp-utils/UploadUtils";
import { humanReadableDate, humanReadableTime } from "../../utils/dateHelper";
import PropTypes from "prop-types";
// Icon
export const IconType = ({ type, color }) => {
  switch (type) {
    case "completed":
      return <CircleCheck className={`text-[var(${color})]`} />;
    case "started":
      return <Mail className="text-[var(--color-system-info-2)]" />;
    case "reminder":
      return <AlarmClock className="text-[var(--color-system-warning-2)]" />;
    case "watching":
      return <Eye className="text-[var(--color-system-warning-2)]" />;
    case "upload":
      return <Upload className="text-[var(--color-system-info-2)]" />;
    case "file":
      return (
        <File
          className={`${
            color
              ? `text-[${color}]` || `text-[var(${color})]`
              : "text-[var(--color-system-info-2)]"
          }`}
        />
      );
    case "clock":
      return <Clock className="text-[var(--color-system-info-2)]" />;
    case "circle-filled":
      return <div className={`w-5 h-5 rounded-full bg-[var(${color})]`}></div>;
    case "circle":
      return null;
  }
};
export const TimelineItem = ({
  title,
  description,
  time,
  username,
  type = "circle",
  color,
  isCompleted = false,
  active = false,
  children,
  isFirst = false,
  isLast = false,
  isProgress = false,
  isDisabled = false,
  className = "",
  titleClassName = "",
  headerClassName = "",
  iconBackgroundColor = "",
}) => {
  let topColor = "bg-[var(--color-neutral-5)]";
  let bottomColor = "bg-[var(--color-neutral-5)]";

  console.log("type", topColor);
  let iconBg = iconBackgroundColor ?? "bg-[var(--color-neutral-1)]";
  let iconDimensions = "w-10 h-10 min-h-10 min-w-10";
  let topHeight = "h-12";
  let cardMargin = isFirst ? "mt-0" : "mt-6";

  if (isProgress) {
    iconDimensions = "w-8 h-8";

    if (isCompleted || isDisabled) {
      type = "circle-filled";
    }
    if (isCompleted) {
      topColor = `bg-[var(${color})]`;
      if (!active) {
        bottomColor = `bg-[var(${color})]`;
      }
      iconBg = `bg-transparent border-2 border-[var(${color})]`;
    } else {
      iconBg = "bg-transparent border-2 border-[var(--color-neutral-5)]";
    }
  }

  if (isFirst) {
    topColor = "bg-transparent";
    topHeight = "h-6";
  }
  if (isLast) {
    bottomColor = "bg-transparent";
  }
  if (isProgress) {
    cardMargin = "mb-10";
    topHeight = "h-2";
    if (isLast) {
      cardMargin = "mb-0";
    }
  }
  const sideTopCSS = `${topColor} ${topHeight}`;
  return (
    <div className="flex gap-8 items-stretch">
      <div className="items-center flex flex-col justify-stretch">
        <div className={`w-0.5  ${sideTopCSS}`}></div>
        <div
          className={`flex items-center justify-center rounded-full ${iconBg} ${iconDimensions}`}
        >
          <IconType type={type} completed={isCompleted} color={color} />
        </div>
        <div className={`w-0.5 flex-1 ${bottomColor}`}></div>
      </div>
      {isProgress ? (
        <div className={`${cardMargin} text-[var(--color-neutral-5)]`}>
          <h4 className="font-semibold">{title}</h4>
          <p className="leading-1 text-[var(--color-neutral-5)]">{children}</p>
        </div>
      ) : (
        <Card className={`bg-[var(--color-neutral-1)] flex-1 ${cardMargin}`}>
          <CardHeader className={headerClassName}>
            <div className="flex justify-between w-full">
              <div className="flex-1">
                <CardTitle className={titleClassName}>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
              </div>
              <div className="flex flex-col">
                <DotSeprated
                  right={humanReadableDate(time)}
                  left={humanReadableTime(time)}
                />
                {username}
              </div>
            </div>
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      )}
    </div>
  );
};
export const Timeline = ({
  children,
  isProgress = false,
  isDisabled = false,
  heading,
  heeadingClassname,
}) => {
  const count = React.Children.count(children);
  let activeCounted = false;
  let headingColor = "text-[var(--color-neutral-5)]";
  if (isProgress) {
    headingColor = "text-[var(--color-neutral-1)]";
  }
  const color = isDisabled ? "--color-neutral-1" : "--color-misc-progress";
   const childrenArray = React.Children.toArray(children);
   const hasActive = childrenArray.some(
    (child) => React.isValidElement(child) && child.props.active
  );
  return (
    <div className={`${!isProgress ? "p-3" : ""}`}>
      {heading && (
        <SectionHeader
          text={heading}
          headingColor={headingColor}
          className={heeadingClassname}
        />
      )}
      <div className="flex flex-col pt-3">
        {React.Children.map(children, (child, index) => {
          const isFirst = index === 0;
          const isLast = index === count - 1;
          const isCompleted = isProgress && hasActive && !activeCounted;
          if (!isDisabled && child?.props.active) {
            activeCounted = true;
          }

          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              isProgress,
              isFirst,
              isLast,
              isCompleted,
              color,
              isDisabled,
            });
          }
        })}
      </div>
    </div>
  );
};

IconType.propTypes = {
  type: PropTypes.oneOf([
    "completed",
    "started",
    "reminder",
    "watching",
    "upload",
    "file",
    "circle-filled",
    "circle",
  ]),
  color: PropTypes.string,
};

TimelineItem.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  time: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  username: PropTypes.string,
  type: PropTypes.oneOf([
    "completed",
    "started",
    "reminder",
    "watching",
    "upload",
    "file",
    "circle-filled",
    "circle",
  ]),
  color: PropTypes.string,
  isCompleted: PropTypes.bool,
  active: PropTypes.bool,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  isProgress: PropTypes.bool,
  isDisabled: PropTypes.bool,
  children: PropTypes.node,
};

Timeline.propTypes = {
  children: PropTypes.node,
  isProgress: PropTypes.bool,
  isDisabled: PropTypes.bool,
  heading: PropTypes.string,
};
